import { useTranslation } from '../presentation/common/useTranslation.js';
import { useState, useEffect } from 'react';

import { X, Mail, Copy, Check, Share2 } from 'lucide-react';

export default function ShareModal({ isOpen, onClose }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [isNativeShareSupported, setIsNativeShareSupported] = useState(false);

  const inviteUrl = `${window.location.origin}/register`;
  const inviteSubject = t('share.invite_subject', 'Einladung zu Goodvale');
  const inviteText = t('share.invite_text', 'Tritt Goodvale bei und werde ein legendärer Abenteurer! Registriere dich hier:');
  const shareMessage = `${inviteText}\n\n${inviteUrl}`;

  useEffect(() => {
    if (typeof navigator !== 'undefined' && navigator.share) {
      setIsNativeShareSupported(true);
    }
  }, []);

  if (!isOpen) return null;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleNativeShare = async () => {
    try {
      await navigator.share({
        title: inviteSubject,
        text: inviteText,
        url: inviteUrl,
      });
    } catch (err) {
      // Don't show error if user cancelled the share operation
      if (err.name !== 'AbortError') {
        console.error('Native share failed:', err);
      }
    }
  };

  const emailUrl = `mailto:?subject=${encodeURIComponent(inviteSubject)}&body=${encodeURIComponent(shareMessage)}`;
  const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(inviteUrl)}&text=${encodeURIComponent(inviteText)}`;
  const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareMessage)}`;

  return (
    <div className="world-modal-overlay fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-in fade-in duration-200">
      <div className="relative w-full max-w-md bg-zinc-950 border border-zinc-800 p-6 md:p-8 shadow-2xl space-y-6 animate-in zoom-in-95">
        
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 text-zinc-400 hover:text-zinc-100 transition-colors p-1"
          aria-label="Close modal"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="space-y-1.5 pr-8">
          <h3 className="text-lg font-bold uppercase tracking-wider text-zinc-100">
            {t('share.title', 'Freunde einladen')}
          </h3>
          <p className="text-xs text-zinc-400">
            {t('share.subtitle', 'Lade andere Abenteurer ein, sich der Gilde anzuschließen und die Welt zu erkunden!')}
          </p>
        </div>

        {/* Copy Link Input */}
        <div className="space-y-2">
          <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
            {t('share.copy_link', 'Link kopieren')}
          </span>
          <div className="flex gap-2">
            <input
              type="text"
              readOnly
              value={inviteUrl}
              className="flex-1 min-w-0 bg-zinc-900 border border-zinc-800 px-3 py-2 text-xs font-mono text-zinc-300 focus:outline-none focus:border-zinc-700"
            />
            <button
              onClick={handleCopyLink}
              className="px-4 py-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 hover:border-zinc-700 text-zinc-300 hover:text-zinc-100 transition-colors flex items-center justify-center gap-1.5 min-w-[100px]"
            >
              {copied ? (
                <>
                  <Check className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-xs font-semibold text-emerald-400">
                    {t('share.link_copied', 'Kopiert!')}
                  </span>
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5" />
                  <span className="text-xs font-semibold">
                    {t('share.copy_link', 'Kopieren')}
                  </span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Share buttons grid */}
        <div className="grid grid-cols-3 gap-3">
          {/* Email */}
          <a
            href={emailUrl}
            className="flex flex-col items-center justify-center p-3 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 hover:border-zinc-700 transition-colors group space-y-2"
          >
            <div className="w-10 h-10 rounded-full bg-zinc-950 border border-zinc-800 flex items-center justify-center text-zinc-400 group-hover:text-zinc-100 transition-colors">
              <Mail className="w-5 h-5" />
            </div>
            <span className="text-[10px] font-medium text-zinc-400 group-hover:text-zinc-200 text-center">
              E-Mail
            </span>
          </a>

          {/* Telegram */}
          <a
            href={telegramUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center justify-center p-3 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 hover:border-zinc-700 transition-colors group space-y-2"
          >
            <div className="w-10 h-10 rounded-full bg-zinc-950 border border-zinc-800 flex items-center justify-center text-zinc-400 group-hover:text-zinc-100 transition-colors">
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                <path d="M20.665 3.717l-17.73 6.837c-1.21.486-1.203 1.161-.222 1.462l4.552 1.42 10.532-6.645c.498-.303.953-.14.579.192l-8.533 7.701-.332 4.978c.488 0 .702-.223.974-.488l2.337-2.27 4.861 3.59c.896.494 1.54.24 1.763-.83l3.19-15.03c.327-1.31-.5-1.905-1.357-1.502z" />
              </svg>
            </div>
            <span className="text-[10px] font-medium text-zinc-400 group-hover:text-zinc-200 text-center">
              Telegram
            </span>
          </a>

          {/* WhatsApp */}
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center justify-center p-3 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 hover:border-zinc-700 transition-colors group space-y-2"
          >
            <div className="w-10 h-10 rounded-full bg-zinc-950 border border-zinc-800 flex items-center justify-center text-zinc-400 group-hover:text-zinc-100 transition-colors">
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                <path d="M12.012 2c-5.506 0-9.989 4.478-9.99 9.984a9.96 9.96 0 0 0 1.333 4.982L2 22l5.233-1.371a9.948 9.948 0 0 0 4.775 1.216h.004c5.502 0 9.986-4.479 9.988-9.987.001-2.669-1.037-5.176-2.93-7.07A9.916 9.916 0 0 0 12.012 2zm5.7 14.13c-.313.88-1.51 1.63-2.08 1.74a4.416 4.416 0 0 1-2.68-.69 13.9 13.9 0 0 1-5.18-5.18c-.85-1.47-1.07-2.82-.41-3.6.22-.26.54-.53.86-.53.25 0 .42.06.6.45.2.46.73 1.79.79 1.9.06.13.06.27-.03.45-.09.18-.18.36-.27.46-.11.12-.22.25-.09.47.28.48.96 1.58 2.06 2.56 1.42 1.26 2.61 1.66 2.99 1.84.28.14.44.09.57-.06.18-.21.79-.92.99-1.24.16-.25.35-.19.57-.11.23.08 1.48.7 1.73.83.25.13.42.19.48.3.06.11.06.7-.25 1.58z" />
              </svg>
            </div>
            <span className="text-[10px] font-medium text-zinc-400 group-hover:text-zinc-200 text-center">
              WhatsApp
            </span>
          </a>
        </div>

        {/* Native Share button (if supported) */}
        {isNativeShareSupported && (
          <button
            onClick={handleNativeShare}
            className="w-full py-2.5 bg-indigo-900 hover:bg-indigo-800 border border-indigo-800 hover:border-indigo-700 text-zinc-100 font-semibold text-xs transition-colors flex items-center justify-center gap-2"
          >
            <Share2 className="w-4 h-4" />
            {t('share.native_share', 'Über andere Apps teilen')}
          </button>
        )}
      </div>
    </div>
  );
}
